import { ISSUE_LANG_KEY } from '@/constants';

/**
 * Which language the reader shows, for the issue and its current article:
 *
 * 1. `requested` -- the issue's language so far (picked inside this issue,
 *    or switched to by rule 4), if the issue has it;
 * 2. otherwise the app-wide language (Settings → General → Language), if
 *    the issue has it;
 * 3. otherwise the issue's first language (languages across all of its
 *    articles).
 * 4. Then, if the current article isn't published in that language, the
 *    article's first language. Callers store the result as the issue's
 *    language, so the switch sticks: Spanish-only article 1, French-only
 *    article 2 → the issue reads in French from article 2 on, and back in
 *    Spanish on article 1.
 *
 * Switching language inside an issue never changes the app-wide language:
 * the issue's language lives in `requested` (the online reader's `?lang=`,
 * the downloaded reader's memory), which the next issue doesn't carry.
 * Returns `appLanguage` while the issue's languages are still unknown.
 */
export function resolveReaderLanguage({
	requested,
	appLanguage,
	issueLanguages,
	articleLanguages,
}: {
	requested?: string;
	appLanguage: string;
	issueLanguages: ReadonlySet<string>;
	/** The current article's languages; omitted or empty skips rule 4. */
	articleLanguages?: ReadonlySet<string>;
}): string {
	let language: string;
	if (requested && issueLanguages.has(requested)) {
		language = requested;
	} else if (!issueLanguages.size || issueLanguages.has(appLanguage)) {
		language = appLanguage;
	} else {
		language = [...issueLanguages][0];
	}
	if (articleLanguages?.size && !articleLanguages.has(language)) {
		return [...articleLanguages][0];
	}
	return language;
}

/**
 * Records the language the reader is showing under `ISSUE_LANG_KEY`. It was
 * read by the old page-view tracking as the page view's language; nothing
 * reads it now (reader events take `content_language` from the call site),
 * and nothing reads it back to pick a reader language -- that's
 * `resolveReaderLanguage`.
 */
export function rememberReaderLanguage(language: string): void {
	try {
		localStorage.setItem(ISSUE_LANG_KEY, language);
	} catch {
		// Private mode etc.: the page view just goes without a language.
	}
}
